import { ChevronLeft, ChevronRight, ExternalLink, Pause, Play, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type WebBanner } from '../context/GlobalContext';
import Logo from './Logo';

const ROTATION_INTERVAL_MS = 7000;

const getSafeRedirectUrl = (value?: string) => {
	if (!value?.trim()) return null;
	try {
		const url = new URL(value.trim());
		return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
	} catch {
		return null;
	}
};

export function StoreAnnouncements({
	announcements,
	includeInactive = false,
	autoRotate = true
}: {
	announcements: WebBanner[];
	includeInactive?: boolean;
	autoRotate?: boolean;
}) {
	const visibleAnnouncements = useMemo(
		() =>
			announcements.filter(
				(announcement) =>
					(includeInactive || announcement.is_active !== false) &&
					(announcement.title?.trim() || announcement.description?.trim())
			),
		[announcements, includeInactive]
	);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isInteracting, setIsInteracting] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
		updatePreference();
		mediaQuery.addEventListener('change', updatePreference);
		return () => mediaQuery.removeEventListener('change', updatePreference);
	}, []);

	useEffect(() => {
		if (currentIndex >= visibleAnnouncements.length) {
			setCurrentIndex(Math.max(0, visibleAnnouncements.length - 1));
		}
	}, [currentIndex, visibleAnnouncements.length]);

	useEffect(() => {
		const checkOverflow = () => {
			if (contentRef.current) {
				setHasOverflow(contentRef.current.scrollHeight > 45);
			}
		};

		const timer = window.setTimeout(checkOverflow, 50);
		window.addEventListener('resize', checkOverflow);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener('resize', checkOverflow);
		};
	}, [currentIndex, visibleAnnouncements]);

	useEffect(() => {
		if (!autoRotate || visibleAnnouncements.length < 2 || isExpanded || isInteracting || isPaused || prefersReducedMotion) {
			return;
		}

		const interval = window.setInterval(() => {
			setCurrentIndex((index) => (index + 1) % visibleAnnouncements.length);
		}, ROTATION_INTERVAL_MS);
		return () => window.clearInterval(interval);
	}, [autoRotate, isExpanded, isInteracting, isPaused, prefersReducedMotion, visibleAnnouncements.length]);

	const announcement = visibleAnnouncements[currentIndex];
	if (!announcement) return null;

	const redirectUrl = getSafeRedirectUrl(announcement.redirect_url);
	const isUrlPrimary = !!redirectUrl && announcement.is_url_primary === true;
	const showExpandButton =
		!isUrlPrimary && (hasOverflow || (announcement.description?.length || 0) > 80 || (announcement.title?.length || 0) > 40);
	const buttonText = announcement.button_text?.trim() || 'Ver detalles';
	const hasMultiple = visibleAnnouncements.length > 1;

	const goToPrevious = () => {
		setCurrentIndex((index) => (index - 1 + visibleAnnouncements.length) % visibleAnnouncements.length);
	};
	const goToNext = () => {
		setCurrentIndex((index) => (index + 1) % visibleAnnouncements.length);
	};

	const actionLink = redirectUrl && (
		<a
			href={redirectUrl}
			target="_blank"
			rel="noopener noreferrer"
			className="inline-flex max-w-[10rem] flex-shrink-0 items-center justify-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-white/90"
		>
			<span className="truncate">{buttonText}</span>
			<ExternalLink className="size-3 flex-shrink-0" />
		</a>
	);

	const pagination = hasMultiple && (
		<div className="flex items-center justify-center gap-3" aria-label="Navegación de comunicados">
			<button
				type="button"
				onClick={goToPrevious}
				className="rounded-full p-1 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
				aria-label="Comunicado anterior"
			>
				<ChevronLeft className="size-4" />
			</button>
			{visibleAnnouncements.length <= 6 ? (
				<div className="flex items-center gap-1.5">
					{visibleAnnouncements.map((item, index) => (
						<button
							type="button"
							key={item.id || index}
							onClick={() => setCurrentIndex(index)}
							className={`rounded-full bg-white transition-all ${index === currentIndex ? 'h-1.5 w-5' : 'size-1.5 opacity-45 hover:opacity-75'}`}
							aria-label={`Ver comunicado ${index + 1}`}
							aria-current={index === currentIndex ? 'true' : undefined}
						/>
					))}
				</div>
			) : (
				<span className="min-w-10 text-center text-xs font-medium tabular-nums text-white/80">
					{currentIndex + 1} de {visibleAnnouncements.length}
				</span>
			)}
			<button
				type="button"
				onClick={goToNext}
				className="rounded-full p-1 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
				aria-label="Siguiente comunicado"
			>
				<ChevronRight className="size-4" />
			</button>
			{autoRotate && !prefersReducedMotion && (
				<button
					type="button"
					onClick={() => setIsPaused((paused) => !paused)}
					className="rounded-full p-1 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
					aria-label={isPaused ? 'Reanudar rotación' : 'Pausar rotación'}
				>
					{isPaused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
				</button>
			)}
		</div>
	);

	if (isExpanded) {
		return (
			<div
				className="relative bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-md"
				onMouseEnter={() => setIsInteracting(true)}
				onMouseLeave={() => setIsInteracting(false)}
				onFocusCapture={() => setIsInteracting(true)}
				onBlurCapture={() => setIsInteracting(false)}
			>
				<div
					key={announcement.id || currentIndex}
					className="mx-auto max-w-4xl px-5 py-4 motion-safe:animate-[fadeInUp_200ms_ease-out]"
				>
					<div className="mb-3 flex justify-center">
						{announcement.image_url ? (
							<div className="overflow-hidden rounded-full bg-white/20">
								<img src={announcement.image_url} alt="" className="size-14 rounded-full object-cover" />
							</div>
						) : (
							<div className="flex items-center justify-center rounded-full bg-white/20 p-2.5">
								<Logo size="min" className="size-6" fill="fill-white" />
							</div>
						)}
					</div>
					{announcement.title && <h3 className="mb-2 text-center text-lg font-medium">{announcement.title}</h3>}
					{announcement.description && (
						<p className="whitespace-pre-wrap text-center text-sm leading-relaxed text-white/90">{announcement.description}</p>
					)}
					{actionLink && <div className="mt-4 flex justify-center">{actionLink}</div>}
					{hasMultiple && <div className="mt-4">{pagination}</div>}
				</div>
				<button
					type="button"
					onClick={() => setIsExpanded(false)}
					className="absolute right-3 top-3 flex items-center justify-center rounded-full bg-white/10 p-1.5 transition-colors hover:bg-white/20"
					aria-label="Contraer comunicado"
				>
					<X className="size-4" />
				</button>
			</div>
		);
	}

	return (
		<div
			className="relative max-h-[110px] overflow-hidden bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-md"
			onMouseEnter={() => setIsInteracting(true)}
			onMouseLeave={() => setIsInteracting(false)}
			onFocusCapture={() => setIsInteracting(true)}
			onBlurCapture={() => setIsInteracting(false)}
		>
			<div
				key={announcement.id || currentIndex}
				className={`mx-auto px-5 motion-safe:animate-[fadeInUp_200ms_ease-out] ${hasMultiple ? 'pb-2 pt-4' : 'py-4'} ${showExpandButton || hasMultiple ? 'max-w-4xl' : 'max-w-max'}`}
			>
				<div className="flex items-center gap-3 md:gap-4">
					{announcement.image_url ? (
						<div className="flex-shrink-0 overflow-hidden rounded-full bg-white/20">
							<img src={announcement.image_url} alt="" className="size-12 rounded-full object-cover md:size-14" />
						</div>
					) : (
						<div className="flex flex-shrink-0 items-center justify-center rounded-full bg-white/20 p-2">
							<Logo size="min" className="size-6" fill="fill-white" />
						</div>
					)}
					<div className="min-w-0 flex-1" ref={contentRef}>
						{announcement.title && <h3 className="truncate text-sm font-medium md:text-base">{announcement.title}</h3>}
						{announcement.description && (
							<p className={`${isUrlPrimary ? 'line-clamp-1' : 'line-clamp-2'} mt-0.5 whitespace-normal text-xs text-white/90`}>
								{announcement.description}
							</p>
						)}
					</div>
					{showExpandButton ? (
						<button
							type="button"
							onClick={() => setIsExpanded(true)}
							className="flex-shrink-0 rounded-full bg-white/20 px-3 py-1 text-xs font-medium transition-colors hover:bg-white/30"
						>
							Leer comunicado
						</button>
					) : (
						actionLink
					)}
				</div>
				{hasMultiple && <div className="mt-0.5">{pagination}</div>}
			</div>
		</div>
	);
}
